import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";

const C = {
  navy: "#1B3A5C", teal: "#2A9D8F", muted: "#6B85A3",
  border: "#D6E4F0", white: "#FFFFFF", lightTeal: "#E8F5F4",
  warning: "#FEF3C7", warningText: "#92400E",
};

interface Props {
  caTotal: number;
  onRecettesSaved: () => void;
}

export default function QuickCatchupBanner({ caTotal, onRecettesSaved }: Props) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [montant, setMontant] = useState("");
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(() =>
    localStorage.getItem("psygestion_catchup_dismissed") === "true"
  );

  if (caTotal > 0 || dismissed) return null;

  const handleSave = async () => {
    if (!user || !montant) return;
    const total = parseInt(montant.replace(/\D/g, ""), 10);
    if (isNaN(total) || total <= 0) {
      toast({ title: "Montant invalide", variant: "destructive" });
      return;
    }

    setSaving(true);
    const mensuel = Math.round(total / 3);
    const year = new Date().getFullYear();

    try {
      const rows = [1, 2, 3].map((mois) => ({
        user_id: user.id,
        date: `${year}-${String(mois).padStart(2, "0")}-15`,
        montant: mensuel,
        description: `Rattrapage CA — ${["janvier", "février", "mars"][mois - 1]} ${year}`,
      }));

      const { error } = await supabase.from("recettes").insert(rows);
      if (error) throw error;

      toast({
        title: "✅ CA enregistré",
        description: `${mensuel.toLocaleString("fr-FR")} €/mois injectés pour Jan-Mar ${year}`,
        duration: 4000,
      });
      setOpen(false);
      onRecettesSaved();
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Banner */}
      <div
        className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl mb-4 cursor-pointer hover:opacity-90 transition-opacity"
        style={{ backgroundColor: C.warning, border: `1px solid #FDE68A` }}
        onClick={() => setOpen(true)}
      >
        <p className="text-sm font-semibold" style={{ color: C.warningText }}>
          📊 Débloquez votre analyse complète : Saisissez votre CA total des 3 derniers mois en 10 secondes →
        </p>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setDismissed(true);
            localStorage.setItem("psygestion_catchup_dismissed", "true");
          }}
          className="text-xs shrink-0 opacity-60 hover:opacity-100"
          style={{ color: C.warningText }}
        >
          ✕
        </button>
      </div>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setOpen(false)}>
          <div
            className="rounded-2xl shadow-xl p-6 w-full max-w-sm mx-4"
            style={{ backgroundColor: C.white }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-1" style={{ color: C.navy }}>
              ⚡ Rattrapage express
            </h3>
            <p className="text-sm mb-4" style={{ color: C.muted }}>
              Combien avez-vous encaissé au total depuis le 1er janvier ?
            </p>

            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Ex : 12 000"
                value={montant}
                onChange={(e) => setMontant(e.target.value)}
                autoFocus
                className="w-full rounded-xl px-4 py-3 text-center font-bold pr-10 outline-none"
                style={{
                  fontSize: 28,
                  border: `2px solid ${montant ? C.teal : C.border}`,
                  color: C.navy,
                  backgroundColor: montant ? C.lightTeal : C.white,
                }}
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-lg" style={{ color: C.muted }}>€</span>
            </div>

            {montant && parseInt(montant.replace(/\D/g, ""), 10) > 0 && (
              <p className="text-xs text-center mb-4" style={{ color: C.muted }}>
                → {Math.round(parseInt(montant.replace(/\D/g, ""), 10) / 3).toLocaleString("fr-FR")} €/mois sera injecté pour Jan, Fév, Mar
              </p>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setOpen(false)}
                className="flex-1 py-3 rounded-xl font-semibold text-sm"
                style={{ color: C.muted, border: `1px solid ${C.border}` }}
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !montant}
                className="flex-1 py-3 rounded-xl font-bold text-sm text-white transition-opacity hover:opacity-90"
                style={{
                  backgroundColor: C.teal,
                  opacity: saving || !montant ? 0.4 : 1,
                }}
              >
                {saving ? "..." : "Valider ✓"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
